import { AgGridContainer } from "~/components/domain/reporting/ag-grid/components/AgGridContainer";
import { GRAY_PALETTE } from "~/styles/palettes";
import { Box, Button, Flex, Paper, Text } from "~/ui";
import type { ActiveReportArtifact, ReportFieldDefinition } from "~/utils/types/assistant-report-artifact";

import {
  buildDefaultColumnDefinition,
  DatasetWarnings,
  recordLineColumnDefinitions,
  recordLineFields,
  recordLineGridHeight,
  recordLineRows,
  reportColumnDefinitions,
  reportFieldMinWidth,
  selectedReportFields,
} from "./AssistantResults";
import classes from "./AssistantResults.module.css";
import { reportArtifactVersionKey } from "./report-column-state";
import { TruncatingTitle } from "./TruncatingTitle";

export const INLINE_MAX_ROWS = 15;
/** AG Grid's theme and root each contribute a 1px border on both sides. */
export const inlineGridContentWidth = (containerWidth: number): number => Math.max(0, containerWidth - 4);
const effectiveFieldWidth = (artifact: ActiveReportArtifact, field: ReportFieldDefinition): number =>
  reportFieldMinWidth(field, artifact.dataset.presentationManifest?.fieldWidths[field.key]);
const effectiveFieldsWidth = (artifact: ActiveReportArtifact, fields: readonly ReportFieldDefinition[]): number =>
  fields.reduce((total, field) => total + effectiveFieldWidth(artifact, field), 0);
export const shouldRenderArtifactInline = (artifact: ActiveReportArtifact, containerWidth: number): boolean => {
  if (containerWidth <= 0 || artifact.dataset.rowCount > INLINE_MAX_ROWS) {
    return false;
  }
  if (effectiveFieldsWidth(artifact, selectedReportFields(artifact)) > containerWidth) {
    return false;
  }
  if (artifact.presentation.mode !== "record") {
    return true;
  }
  const lineFields = recordLineFields(artifact);
  const lineRows = recordLineRows(artifact);
  const hasLineGrid = lineFields.length > 0 && lineRows.length > 0;
  return !hasLineGrid || (lineRows.length <= INLINE_MAX_ROWS && effectiveFieldsWidth(artifact, lineFields) <= containerWidth);
};
interface AssistantInlineResultProps {
  artifact: ActiveReportArtifact;
  onOpen: () => void;
}

// The inline preview exists only to be read: no column menus, no resize, and flex so the
// table fills the card instead of leaving a dead right region. Interrogation (sorting,
// filtering, export) lives in the drawer. One shared object, so a re-render keeps the grid's columns.
const inlineDefaultColDef = {
  ...buildDefaultColumnDefinition(false),
  resizable: false,
  suppressMenu: true,
  suppressHeaderMenuButton: true,
  flex: 1,
};

export const AssistantInlineResult = ({ artifact, onOpen }: AssistantInlineResultProps) => {
  const versionKey = reportArtifactVersionKey(artifact);
  const lineRows = recordLineRows(artifact);
  const lineColumnDefs = recordLineColumnDefinitions(artifact, false, true);
  const isRecord = artifact.presentation.mode === "record";
  const rowData = isRecord ? artifact.dataset.rows.slice(0, 1) : artifact.dataset.rows;
  const columnDefs = reportColumnDefinitions(artifact, false, true);
  // Committed sort and columns come from the column definitions; copy and export live in the panel.

  return (
    <Paper p={0} radius="md" w="100%" style={{ boxShadow: `inset 0 0 0 1px ${GRAY_PALETTE[3]}`, overflow: "hidden" }}>
      <Flex align="center" justify="space-between" gap="sm" p="sm" pb={0} mb="xs">
        <TruncatingTitle
          title={artifact.presentation.title}
          secondary={`${artifact.dataset.rowCount} ${artifact.dataset.rowCount === 1 ? "row" : "rows"}`}
        />
        <Button size="xs" variant="subtle" onClick={onOpen} style={{ flexShrink: 0 }}>
          Open report
        </Button>
      </Flex>
      <Box px="sm">
        <DatasetWarnings warnings={artifact.dataset.warnings} />
      </Box>
      {artifact.dataset.rowCount === 0 ? (
        <Text size="sm" c="gray.6" p="sm" pt={0}>
          Nothing matched this request.
        </Text>
      ) : (
        <>
          <Box className={classes.gridContainer} style={{ maxWidth: "100%", overflow: "hidden" }}>
            <AgGridContainer
              columnDefs={columnDefs}
              columnDefsVersionKey={`${versionKey}:inline`}
              rowData={rowData}
              height={recordLineGridHeight(rowData.length)}
              gridTheme="ag-theme-balham"
              defaultColDef={inlineDefaultColDef}
              suppressRowClickSelection
              suppressContextMenu
              suppressHorizontalScroll
              allowHorizontalOverflow={false}
            />
          </Box>
          {isRecord && lineColumnDefs.length > 0 && lineRows.length > 0 && (
            <Box className={classes.gridContainer} mt="sm" style={{ maxWidth: "100%", overflow: "hidden" }}>
              <AgGridContainer
                columnDefs={lineColumnDefs}
                columnDefsVersionKey={`${versionKey}:inline-lines`}
                rowData={lineRows}
                height={recordLineGridHeight(lineRows.length)}
                gridTheme="ag-theme-balham"
                defaultColDef={inlineDefaultColDef}
                suppressRowClickSelection
                suppressContextMenu
                suppressHorizontalScroll
                allowHorizontalOverflow={false}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};
